import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  MenuItem,
} from "@mui/material";
import {
  Close,
  Delete,
  Campaign as CampaignIcon,
  ContentCopy,
  InfoOutlined,
} from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import StandardPagination, { ITENS_POR_PAGINA_PADRAO } from "../components/Pagination/StandardPagination";
import SavingOverlay from "../components/SavingOverlay";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import {
  crmService,
  Campaign,
  Pipeline,
  LEAD_CONTACT_ORIGINS,
} from "../services/crmService";
import { whatsappChannelsService } from "../services/whatsappChannelsService";
import { colors, typography, pageHeader } from "../theme/designSystem";

// Campanhas de captação (permissão crm-campaigns, só Administrador): ligam um sinal de
// entrada — id do anúncio na Meta ou frase do link wa.me — ao pipeline que recebe o lead.

const NAME_MAX = 120;
const AD_SOURCE_MAX = 80;
const PHRASE_MAX = 200;

const cardSx = {
  backgroundColor: "white",
  borderRadius: "12px",
  padding: "1rem 1.25rem",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  border: "1px solid #e9ecef",
};

const fieldSx = {
  // altura fixa SÓ em linha única: multiline precisa crescer (bug recorrente do projeto)
  "& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)": { height: "40px" },
  "& .MuiOutlinedInput-root": {
    fontSize: "1rem",
    backgroundColor: "white",
    "& fieldset": { borderColor: "#ced4da", legend: { maxWidth: "100%" } },
    "&:hover fieldset": { borderColor: "#ced4da" },
    "&.Mui-focused fieldset": {
      borderColor: colors.primary,
      boxShadow: "0 0 0 3px rgba(3, 180, 198, 0.1)",
    },
  },
  "& .MuiOutlinedInput-input, & .MuiSelect-select": {
    padding: "0.375rem 0.5rem",
    color: colors.textPrimary,
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.95rem",
    color: colors.textSecondary,
    backgroundColor: "white",
    paddingLeft: "4px",
    paddingRight: "4px",
    "&.Mui-focused": { color: colors.primary },
  },
};

const modalTitleSx = {
  backgroundColor: colors.primary,
  color: colors.white,
  padding: "1rem 1.25rem",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const modalPaperSx = { borderRadius: "12px", maxHeight: "85vh" };

interface CampaignForm {
  id: string;
  name: string;
  pipelineId: string;
  adSourceId: string;
  matchPhrase: string;
  contactOrigin: string;
}

const emptyForm: CampaignForm = {
  id: "", name: "", pipelineId: "", adSourceId: "", matchPhrase: "", contactOrigin: "",
};

/** Link pronto para landing, bio ou QR code. Sem número, mostra o formato para o usuário montar. */
const buildWaLink = (phone: string | null, phrase: string): string => {
  const digits = (phone || "").replace(/\D/g, "");
  const target = digits || "55DDNUMERO";
  return `https://wa.me/${target}?text=${encodeURIComponent(phrase)}`;
};

const CrmCampaigns: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  // escopo da entidade da sessão (nunca de parâmetro da URL)
  const entityId = user?.entityId || "";

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"todas" | "ativas" | "inativas">("todas");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITENS_POR_PAGINA_PADRAO);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [channelPhone, setChannelPhone] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<CampaignForm>(emptyForm);
  const [confirmDelete, setConfirmDelete] = useState<Campaign | null>(null);

  const activePipelines = useMemo(() => pipelines.filter((p) => p.active), [pipelines]);

  const load = useCallback(async () => {
    if (!entityId) {
      setError("Sessão sem entidade definida. Refaça o login para continuar.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [list, funnels] = await Promise.all([
        crmService.getCampaigns(entityId, true),
        crmService.getPipelines(entityId, true),
      ]);
      setCampaigns(list);
      setPipelines(funnels);
    } catch {
      setError("Não foi possível carregar as campanhas. Tente novamente.");
    } finally {
      setLoading(false);
    }

    // número do canal só enriquece o link de exemplo — falhar aqui não quebra a tela
    try {
      const channels = await whatsappChannelsService.getByEntity(entityId);
      setChannelPhone(channels.find((c) => c.displayPhone)?.displayPhone ?? null);
    } catch {
      setChannelPhone(null);
    }
  }, [entityId]);

  /** Minusculas e sem acento: quem busca "captacao" espera achar "Captação". */
  const normalize = (v: string) =>
    v.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

  const filtered = campaigns.filter((c) => {
    if (statusFilter !== "todas" && c.active !== (statusFilter === "ativas")) {
      return false;
    }
    return !search.trim() || normalize(c.name || "").includes(normalize(search));
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paged = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("todas");
  };

  const hasFilterChanges = search !== "" || statusFilter !== "todas";

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    const fallback = activePipelines.find((p) => p.isDefault) || activePipelines[0];
    setForm({ ...emptyForm, pipelineId: fallback?.id || "" });
    setFormOpen(true);
  };

  const openEdit = (campaign: Campaign) => {
    setForm({
      id: campaign.id,
      name: campaign.name,
      pipelineId: campaign.pipelineId,
      adSourceId: campaign.adSourceId || "",
      matchPhrase: campaign.matchPhrase || "",
      contactOrigin: campaign.contactOrigin || "",
    });
    setFormOpen(true);
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) {
      showToast("Informe o nome da campanha.", "error");
      return;
    }
    if (!form.pipelineId) {
      showToast("Escolha o pipeline que recebe os leads da campanha.", "error");
      return;
    }
    if (!form.adSourceId.trim() && !form.matchPhrase.trim()) {
      showToast("Informe ao menos um sinal: o id do anúncio ou a frase do link.", "error");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name,
        pipelineId: form.pipelineId,
        adSourceId: form.adSourceId.trim() || null,
        matchPhrase: form.matchPhrase.trim() || null,
        contactOrigin: form.contactOrigin || null,
      };
      if (form.id) {
        await crmService.updateCampaign(form.id, entityId, payload);
      } else {
        await crmService.createCampaign(entityId, payload);
      }
      setFormOpen(false);
      showToast("Campanha salva.", "success");
      await load();
    } catch (err: any) {
      showToast(err?.response?.data?.description || "Não foi possível salvar a campanha.", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (campaign: Campaign) => {
    setSaving(true);
    try {
      await crmService.setCampaignActive(campaign.id, entityId, !campaign.active);
      await load();
    } catch (err: any) {
      showToast(err?.response?.data?.description || "Não foi possível alterar o status.", "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirmDelete) {
      return;
    }
    setSaving(true);
    try {
      await crmService.deleteCampaign(confirmDelete.id, entityId);
      setConfirmDelete(null);
      showToast("Campanha excluída.", "success");
      await load();
    } catch (err: any) {
      showToast(err?.response?.data?.description || "Não foi possível excluir a campanha.", "error");
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async (phrase: string) => {
    try {
      await navigator.clipboard.writeText(buildWaLink(channelPhone, phrase));
      showToast("Link copiado.", "success");
    } catch {
      showToast("Não foi possível copiar — selecione o link e copie manualmente.", "error");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Campanhas do CRM
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Cada campanha liga um sinal de entrada — o id do anúncio no Facebook/Instagram ou a
                frase do link do WhatsApp — a um pipeline. Quem chega por ela cai no pipeline certo e
                já nasce com a origem preenchida.
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1 }}>
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Busca por nome"
                  placeholder="Buscar campanha"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  inputProps={{ maxLength: 120 }}
                />
                <TextField
                  select
                  size="small"
                  label="Situação"
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as "todas" | "ativas" | "inativas")
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                >
                  <MenuItem value="todas">Todas</MenuItem>
                  <MenuItem value="ativas">Ativas</MenuItem>
                  <MenuItem value="inativas">Inativas</MenuItem>
                </TextField>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                <AddButton onClick={openCreate} title="Nova campanha" />
              </div>
            </div>

            <ResultCount shown={filtered.length} total={campaigns.length} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={32} sx={{ color: colors.primary }} />
            </Box>
          ) : error ? (
            <Paper elevation={0} sx={{ ...cardSx, textAlign: "center", color: colors.error }}>
              {error}
            </Paper>
          ) : filtered.length === 0 ? (
            <Paper elevation={0} sx={{ ...cardSx, textAlign: "center", py: 5 }}>
              <CampaignIcon sx={{ fontSize: 40, color: colors.textMuted, mb: 1 }} />
              <Typography sx={{ fontWeight: typography.fontWeight.semibold, mb: 0.5 }}>
                Nenhuma campanha cadastrada
              </Typography>
              <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                Sem campanha, todo lead cai no pipeline padrão do canal. Crie a primeira no botão "+".
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {paged.map((campaign) => (
                <Paper
                  key={campaign.id}
                  elevation={0}
                  onClick={() => openEdit(campaign)}
                  sx={{
                    ...cardSx,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1.5,
                    cursor: "pointer",
                    opacity: campaign.active ? 1 : 0.6,
                    "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.12)" },
                  }}
                >
                  <CampaignIcon sx={{ color: colors.primary, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0, flex: "1 1 260px" }}>
                    <Typography sx={{ fontWeight: typography.fontWeight.semibold, color: colors.textPrimary }}>
                      {campaign.name}
                    </Typography>
                    <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                      Pipeline: {campaign.pipelineName} · {campaign.leads} lead
                      {campaign.leads === 1 ? "" : "s"}
                      {campaign.contactOrigin
                        ? ` · Origem: ${LEAD_CONTACT_ORIGINS[campaign.contactOrigin] || campaign.contactOrigin}`
                        : ""}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                      {campaign.adSourceId ? (
                        <Chip
                          size="small"
                          label={`Anúncio ${campaign.adSourceId}`}
                          sx={{ fontSize: typography.fontSize.xs, backgroundColor: "#e7f5ff", color: "#1971c2" }}
                        />
                      ) : null}
                      {campaign.matchPhrase ? (
                        <Chip
                          size="small"
                          label={`"${campaign.matchPhrase}"`}
                          sx={{ fontSize: typography.fontSize.xs, backgroundColor: "#f3f0ff", color: "#5f3dc4" }}
                        />
                      ) : null}
                    </Box>
                  </Box>

                  <Chip
                    label={campaign.active ? "Ativa" : "Inativa"}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleActive(campaign);
                    }}
                    sx={{
                      cursor: "pointer",
                      fontWeight: typography.fontWeight.semibold,
                      backgroundColor: campaign.active ? "#d3f9d8" : "#f1f3f5",
                      color: campaign.active ? "#2b8a3e" : colors.textSecondary,
                    }}
                  />

                  <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                    {campaign.matchPhrase ? (
                      <Tooltip title="Copiar link do WhatsApp desta campanha">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyLink(campaign.matchPhrase || "");
                          }}
                          sx={{ width: { xs: 40, sm: 32 }, height: { xs: 40, sm: 32 } }}
                        >
                          <ContentCopy sx={{ fontSize: 18, color: colors.textSecondary }} />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    <Tooltip title="Excluir campanha">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDelete(campaign);
                        }}
                        sx={{ width: { xs: 40, sm: 32 }, height: { xs: 40, sm: 32 } }}
                      >
                        <Delete sx={{ fontSize: 18, color: colors.error }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}

            {filtered.length > 0 && (
              <StandardPagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={filtered.length}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(n) => {
                  setItemsPerPage(n);
                  setCurrentPage(1);
                }}
              />
            )}
          </div>
        </Container>
      </Box>

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: modalPaperSx }}
      >
        <DialogTitle sx={modalTitleSx}>
          <Typography sx={{ fontWeight: typography.fontWeight.semibold }}>
            {form.id ? "Editar campanha" : "Nova campanha"}
          </Typography>
          <IconButton onClick={() => setFormOpen(false)} sx={{ color: colors.white }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "1.25rem !important", display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Nome da campanha"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            inputProps={{ maxLength: NAME_MAX }}
            fullWidth
            size="small"
            sx={fieldSx}
          />

          <TextField
            select
            label="Pipeline de destino"
            value={form.pipelineId}
            onChange={(e) => setForm({ ...form, pipelineId: e.target.value })}
            fullWidth
            size="small"
            sx={fieldSx}
          >
            {activePipelines.map((pipeline) => (
              <MenuItem key={pipeline.id} value={pipeline.id}>
                {pipeline.name}
                {pipeline.isDefault ? " (padrão)" : ""}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Id do anúncio (Facebook/Instagram)"
            value={form.adSourceId}
            onChange={(e) => setForm({ ...form, adSourceId: e.target.value })}
            inputProps={{ maxLength: AD_SOURCE_MAX }}
            helperText="Quem clica num anúncio Click-to-WhatsApp traz esse id junto da mensagem. É o sinal mais confiável."
            fullWidth
            size="small"
            sx={fieldSx}
          />

          <TextField
            label="Frase do link"
            value={form.matchPhrase}
            onChange={(e) => setForm({ ...form, matchPhrase: e.target.value })}
            inputProps={{ maxLength: PHRASE_MAX }}
            helperText="Trecho que já vai escrito na mensagem quando a pessoa clica no seu link. Use frases distintas por canal."
            fullWidth
            size="small"
            sx={fieldSx}
          />

          {form.matchPhrase.trim() ? (
            <Paper
              elevation={0}
              sx={{
                backgroundColor: "#f8f9fa",
                border: "1px solid #e9ecef",
                borderRadius: "8px",
                padding: "0.75rem",
              }}
            >
              <Box className="c4u-info-box">
                <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
                <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>
                  Link para a landing, a bio do Instagram ou o QR code
                  {channelPhone ? "" : " — troque 55DDNUMERO pelo número do seu canal"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: typography.fontSize.xs,
                    fontFamily: "monospace",
                    color: colors.textPrimary,
                    wordBreak: "break-all",
                    flex: 1,
                  }}
                >
                  {buildWaLink(channelPhone, form.matchPhrase.trim())}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => copyLink(form.matchPhrase.trim())}
                  sx={{ flexShrink: 0, width: { xs: 40, sm: 32 }, height: { xs: 40, sm: 32 } }}
                >
                  <ContentCopy sx={{ fontSize: 18, color: colors.textSecondary }} />
                </IconButton>
              </Box>
            </Paper>
          ) : null}

          <TextField
            select
            label="Origem a registrar no lead"
            value={form.contactOrigin}
            onChange={(e) => setForm({ ...form, contactOrigin: e.target.value })}
            helperText="Preenchida automaticamente no lead — a clínica não precisa perguntar de onde a pessoa veio."
            fullWidth
            size="small"
            sx={fieldSx}
          >
            <MenuItem value="">Não registrar</MenuItem>
            {Object.entries(LEAD_CONTACT_ORIGINS).map(([code, label]) => (
              <MenuItem key={code} value={code}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={() => setFormOpen(false)} sx={{ color: colors.textSecondary }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={save}
            sx={{ backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Excluir campanha"
        message={`Excluir a campanha "${confirmDelete?.name}"? Os leads que já entraram por ela continuam nos pipelines onde estão. Para encerrar a campanha mantendo o histórico, prefira inativá-la.`}
        type="danger"
        onConfirm={remove}
        onCancel={() => setConfirmDelete(null)}
      />

      <SavingOverlay open={saving} />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
      <FooterInternal />
    </Box>
  );
};

export default CrmCampaigns;
